import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RunbookLog } from 'infrastructure/classes/runbook-log';
import { STALE_TIME } from 'infrastructure/consts/stale-time';
import { arrUniqEl } from 'infrastructure/functions';

import useApi from '../use-api';

import type {
  IPaginationMeta,
  IPartialPaginationMeta,
  IRunbookLogPaginatedData,
} from 'infrastructure/interfaces';

const runbookLogsApi = 'core/runbook-logs';

export const runbookLogsLoadKey = 'loadRunbookLogs';

export interface IUseRunbookLogMeta {
  clinicGuid?: string;
  reverse?: string;
}

type mergedMeta = IUseRunbookLogMeta & IPaginationMeta;

type UseRunbookLog = {
  runbookLogs: { items: Array<RunbookLog>; count: number } | undefined;
  loading: boolean;
  meta: mergedMeta;
  loadRunbookLogs: (
    meta: mergedMeta,
    reset?: boolean,
  ) => Promise<Array<RunbookLog>>;
};

interface IUseRunbookLogProps {
  meta?: IUseRunbookLogMeta & IPartialPaginationMeta;
}

const getRunbookQueryKey = (meta: mergedMeta) => [
  runbookLogsLoadKey,
  meta.clinicGuid,
  meta.page,
  meta.items,
  meta.reverse,
];

export const useRunbookLog = (props?: IUseRunbookLogProps): UseRunbookLog => {
  const options = props || {};
  const metaProps = options.meta || {};

  const { loading, isMounted, loadData } = useApi();

  const queryClient = useQueryClient();

  const [runbookLogs, setRunbookLogs] = useState<Array<RunbookLog>>([]);
  const [meta, setMeta] = useState<mergedMeta>({
    page: 1,
    items: 1,
    totalCount: 0,
    ...metaProps,
  });

  const loadRunbookLogs = async (filters: mergedMeta, reset = false) => {
    const data = await queryClient.fetchQuery(
      getRunbookQueryKey(filters),
      () =>
        loadData<
          IRunbookLogPaginatedData,
          Omit<IUseRunbookLogMeta, 'sortField'>
        >(runbookLogsApi, {
          guidValue: filters.clinicGuid,
          page: filters.page,
          items: filters.items,
        }).then((res) => {
          if (res) {
            const { data: items, count } = res;
            return {
              items: items.map(
                (el) => new RunbookLog(el.newValue, el.createdAt),
              ),
              count,
            };
          }
        }),
      { staleTime: STALE_TIME },
    );

    if (isMounted && data) {
      setMeta({
        ...meta,
        ...filters,
        totalCount: data.count,
      });

      const newRows = reset
        ? data.items
        : arrUniqEl([...runbookLogs, ...data.items], 'createdAt');

      setRunbookLogs(newRows);

      return newRows;
    }
    return [];
  };

  const clinicGuid = meta.clinicGuid || props?.meta?.clinicGuid;

  const { data: response, isLoading } = useQuery({
    queryKey: getRunbookQueryKey({ ...meta, clinicGuid }),
    queryFn: () => {
      if (clinicGuid) {
        const result = loadData<
          IRunbookLogPaginatedData,
          Omit<IUseRunbookLogMeta, 'sortField'>
        >(runbookLogsApi, {
          guidValue: clinicGuid,
          page: meta.page,
          items: meta.items,
        }).then((res) => {
          if (res) {
            const { data: items, count } = res;
            return {
              items: items.map(
                (el) => new RunbookLog(el.newValue, el.createdAt),
              ),
              count,
            };
          }
        });

        return result;
      }
    },
    staleTime: STALE_TIME,
    enabled: Boolean(clinicGuid),
  });

  return {
    runbookLogs: response,
    loading: loading || isLoading,
    meta,
    loadRunbookLogs,
  };
};
